import classNames from "classnames";
import { isEmpty, map } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import queryString from "query-string";
import React, { Component } from "react";
import { connect } from "react-redux";
import { createSelector } from "reselect";

import {
  CAMPAIGN_ACTIONS,
  CAMPAIGN_STATUSES,
  CAMPAIGN_STEPS,
  CAMPAIGN_TYPE,
  THEATRE_CRITERIA_MENU_LIST,
  THEATRE_CRITERIA_TYPES,
} from "../../../../../constants";
import campaignFormStyles from "../../../../../styles/CampaignCreate/CampaignForm.module.scss";
import styles from "../../../../../styles/CampaignCreate/TargetGroup.module.scss";
import {
  bindDispatch,
  getParams,
  showToast,
  validateLocationCriteria,
  validateTheatreCriteria,
} from "../../../../../utils";
import Breadcrumb from "../../../../common/BreadCrumb";
import InfoBlockLoading from "../../../../common/InfoBlockLoading";
import PageLoadingWithTabs from "../../../../common/PageLoadingWithTabs";
import SplitTargetGroup from "../../bulkActions/SplitCampaigns";
import CancelCampaignTargetGroup from "../../bulkActions/CancelCampaigns";
import PauseOrResumeActions from "../../bulkActions/PauseOrResumeActions";
import CampaignTree from "../../CampaignStructure/CampaignTree";
import CampaignPageHeader from "../../common/CampaignPageHeader";
import TargetGroupList from "../../common/pages/TargetGroupDetails";
import WithFetchList from "../../../../hoc/withFetchList";
import { campaignStepChange, validateMandatoryTargetGroupFields } from "../../utils/campaignCreate";
import { getCurrentActionPeriod, isCampaignStatusChangesAvailable } from "../../utils";
import CampaignSteps from "../CampaignSteps";
import EmptyState from "../EmptyState";
import CreateTargetGroup from "./CreateTargetGroup";
import campaignRouteHandler from "..";

class CampaignCreateTargetGroupList extends Component {
  state = {
    openedBulkAction: null,
    currentTargetGroup: {},
  };

  getListParams = () => {
    const { ps, page, sort, match } = this.props;
    return { ...getParams({ ps, page, sort }), campaignId: match.params.campaignId };
  };

  openTargetGroupAction = (action, targetGroup) =>
    this.setState({ openedBulkAction: action, currentTargetGroup: targetGroup });

  closeBulkAction = () => this.setState({ openedBulkAction: null });

  handleClearPause = async (targetGroup) => {
    const { actions, filters } = this.props;
    await actions.pauseOrResumeTargetGroups(
      [targetGroup.id],
      CAMPAIGN_ACTIONS.PAUSE,
      { clearPause: true },
      this.getListParams(),
      filters,
      false
    );
    this.props.fetchData();
  };

  handleClearCancel = async (targetGroup) => {
    const { actions, filters } = this.props;
    await actions.cancelCampaigns(
      { ids: [targetGroup.id], fromDate: "", clearCancellation: true },
      CAMPAIGN_TYPE.TARGET_GROUP,
      this.getListParams(),
      () => {},
      false,
      filters,
      false
    );
    this.props.fetchData();
  };

  handlePauseOrResume = async (period) => {
    const { actions, filters } = this.props;
    const { currentTargetGroup, openedBulkAction } = this.state;
    await actions.pauseOrResumeTargetGroups(
      [currentTargetGroup.id],
      openedBulkAction,
      period,
      this.getListParams(),
      filters,
      false
    );
    this.props.fetchData();
  };

  handleCancel = async (data) => {
    const { actions, filters } = this.props;
    const { currentTargetGroup } = this.state;
    await actions.cancelCampaigns(
      { ...data, ids: [currentTargetGroup.id] },
      CAMPAIGN_TYPE.TARGET_GROUP,
      this.getListParams(),
      this.closeBulkAction,
      false,
      filters,
      false
    );
    this.props.fetchData();
  };

  renderTargetGroupActionPanels = () => {
    const { openedBulkAction, currentTargetGroup } = this.state;
    const { history } = this.props;

    if (openedBulkAction === CAMPAIGN_ACTIONS.CANCEL) {
      return (
        <CancelCampaignTargetGroup
          selected={[currentTargetGroup]}
          header="RightPanelHeader.cancelTargetGroup"
          isOpen
          actionType={openedBulkAction}
          onClose={this.closeBulkAction}
          cancelCampaigns={this.handleCancel}
        />
      );
    }

    if (
      openedBulkAction === CAMPAIGN_ACTIONS.PAUSE ||
      openedBulkAction === CAMPAIGN_ACTIONS.RESUME
    ) {
      const isPaused = openedBulkAction === CAMPAIGN_ACTIONS.PAUSE;
      const status = isPaused ? CAMPAIGN_STATUSES.PAUSED : CAMPAIGN_STATUSES.ACTIVE;
      const isEdit = isCampaignStatusChangesAvailable([currentTargetGroup], status);
      const header = isPaused
        ? isEdit
          ? "RightPanelHeader.editPauseTargetGroup"
          : "RightPanelHeader.pauseTargetGroup"
        : isEdit
        ? "RightPanelHeader.editResumeTargetGroup"
        : "RightPanelHeader.resumeTargetGroup";
      return (
        <PauseOrResumeActions
          header={header}
          selectionInfo={null}
          history={history}
          selected={[currentTargetGroup]}
          period={getCurrentActionPeriod(currentTargetGroup, status)}
          isPaused={isPaused}
          isEdit={isEdit}
          showEdit
          isOpen
          actionType={openedBulkAction}
          onClose={this.closeBulkAction}
          onPauseOrResumeAction={this.handlePauseOrResume}
        />
      );
    }

    return null;
  };

  render = () => (
    <>
      <TargetGroupList
        {...this.props}
        serverBacked
        showActionMenu
        onCancel={(targetGroup) => this.openTargetGroupAction(CAMPAIGN_ACTIONS.CANCEL, targetGroup)}
        onPause={(targetGroup) => this.openTargetGroupAction(CAMPAIGN_ACTIONS.PAUSE, targetGroup)}
        onResume={(targetGroup) => this.openTargetGroupAction(CAMPAIGN_ACTIONS.RESUME, targetGroup)}
        onClearCancel={this.handleClearCancel}
        onClearPause={this.handleClearPause}
      />
      {this.renderTargetGroupActionPanels()}
    </>
  );
}

export const CampaignCreateTargetGroupListWithFilters = WithFetchList(
  "getNewCampaignTargetGroups",
  {
    ps: 50,
    sort: [{ id: "updatedAt", desc: true }],
    searchParam: "q",
    requestParams: ({ campaignId }) => ({ campaignId }),
  }
)(CampaignCreateTargetGroupList);

export class TargetGroup extends Component {
  getInitialState = () => {
    const {
      campaignCreate: { campaign },
    } = this.props;
    const today = moment().startOf("day");

    return {
      targetGroup: {
        name: "",
        validity: {
          fromDate:
            campaign && campaign.validity
              ? moment.max(moment(campaign.validity.fromDate), today).format("YYYY-MM-DD")
              : today.format("YYYY-MM-DD"),
          toDate:
            campaign && campaign.validity
              ? moment(campaign.validity.toDate).format("YYYY-MM-DD")
              : null,
        },
        criteria: {
          inclusions: [],
          exclusions: [],
        },
        maxPlaysPerScreen: 0,
        budgetedPlays: 0,
        status: CAMPAIGN_STATUSES.DRAFT,
      },
      isEdit: false,
      hasFormSaved: false,
      isFormEdited: false,
      isFetchingPreferences: false,
      isValidTargetGroup: true,
      isPageLoading: !this.props.isCampaignInvalid,
      isSplitPanelOpen: false,
      targetGroupSplitData: { id: "", validity: {} },
    };
  };

  state = this.getInitialState();

  containerRef = React.createRef();

  static propTypes = {
    isCampaignInvalid: PropTypes.bool,
  };

  static defaultProps = {
    isCampaignInvalid: false,
  };

  static defaultProps = {
    campaign: null,
  };

  componentDidMount = async () => {
    //this.props.match.params.action = "list";
    const {
      match: {
        params: { action },
      },
      campaignCreate: { campaign, campaignTargetGroup },
      actions,
      history,
      isCampaignInvalid,
    } = this.props;
    let targetGroupData = this.state.targetGroup;
    const query = queryString.parse(history.location.search);
    await actions.updateActiveStep({
      campaignStep: CAMPAIGN_STEPS.TARGET_GROUP,
      campaignId: campaign.id,
    });

    if (isCampaignInvalid) return false;

    if (action === "create") {
      if (query?.id) {
        await actions.getTargetGroupById(query?.id, "duplicate");
        if (isEmpty(campaignTargetGroup)) {
          this.setState({
            isValidTargetGroup: false,
            isPageLoading: false,
          });
        } else {
          targetGroupData = campaignTargetGroup;
        }
      } else {
        targetGroupData = {
          ...targetGroupData,
          validity: campaign.validity,
          maxPlaysPerScreen: campaign.maxPlaysPerScreen,
        };
        await actions.updateNewCampaignTargetGroup(targetGroupData, "create");
      }
    } else if (action === "list") {
      await actions.updateTargetGroupAction(action);
    } else {
      const targetGroup = await actions.getTargetGroupById(action, "edit");
      if (isEmpty(targetGroup)) {
        this.setState({
          isValidTargetGroup: false,
          isPageLoading: false,
        });
      } else {
        targetGroupData = targetGroup;
      }
    }

    this.setState({
      targetGroup: targetGroupData,
      isPageLoading: false,
    });
  };

  componentDidUpdate(prevProps) {
    const {
      match: { params },
      campaignCreate: { targetGroupAction, campaignTargetGroup },
      history,
      actions,
    } = this.props;

    // if (
    //   campaignTargetGroups?.data?.length === 0 &&
    //   prevProps.campaignCreate.campaignTargetGroups?.data?.length !==
    //     campaignTargetGroups?.data?.length
    // ) {
    //   if (targetGroupAction === "list") {
    //     history.push(`/campaign-create/${params.campaignId}/target-groups/create`);
    //     actions.updateTargetGroupAction("create");
    //   }
    // }

    // Detect changes in action (create, list)
    if (prevProps.match.params.action !== params.action) {
      if (params.action === "list") {
        this.setState({ targetGroup: this.getInitialState().targetGroup });
        if (targetGroupAction !== "list") {
          actions.updateTargetGroupAction("list");
          actions.getNewCampaignTargetGroups(params);
        }
      } else if (params.action === "create") {
        this.handleTargetGroupCreate();
      }
    }

    if (prevProps.campaignCreate.targetGroupAction !== targetGroupAction) {
      if (targetGroupAction === "list") {
        history.push(`/campaign-create/${params.campaignId}/target-groups/list`);
        this.setState({ targetGroup: this.getInitialState().targetGroup });
      }

      if (targetGroupAction === "edit" || targetGroupAction === "duplicate") {
        if (targetGroupAction === "duplicate") {
          campaignTargetGroup.status = CAMPAIGN_STATUSES.DRAFT;
        }
        if (!params.id && targetGroupAction === "edit")
          history.push(
            `/campaign-create/${params.campaignId}/target-groups/${campaignTargetGroup.id}`
          );
        this.setState({
          targetGroup: campaignTargetGroup,
        });
      }
    }
  }

  onInputChange = (data, key) => {
    //SLATE-1546 Feb7 Anushree:- Utilizing functional setState and spread operator for non-mutative state updates.
    this.setState((prevState) => {
      const targetGroup = { ...prevState.targetGroup };
      if (key === "validity") {
        const [fromDate, toDate] = data;
        const validity = {
          fromDate: fromDate
            ? moment(fromDate).format("YYYY-MM-DD")
            : targetGroup.validity?.fromDate ?? null,
          toDate: toDate
            ? moment(toDate).format("YYYY-MM-DD")
            : targetGroup.validity?.toDate ?? null,
        };
        targetGroup[key] = validity;
      } else {
        targetGroup[key] = data;
      }
      return {
        targetGroup,
        isFormEdited: true,
      };
    });
  };

  updateDefaultValues = (data) => {
    const currentItem = THEATRE_CRITERIA_MENU_LIST.find((item) => item.id === data.type);
    let criteriaValues = {};
    //default values for auditoriumDimensions
    map(currentItem.accessors, (key) => {
      criteriaValues[key] = {
        rangeFrom: data[key].rangeFrom || 0,
        rangeTo: data[key].rangeTo || Infinity,
      };
    });
    //SLATE-1434 Dec11 Anushree:- default value for ticketPrice and seatingCapacity added
    if (
      currentItem?.id === THEATRE_CRITERIA_TYPES.TICKET_PRICE ||
      currentItem?.id === THEATRE_CRITERIA_TYPES.SEATING_CAPACITY
    ) {
      criteriaValues = {
        rangeFrom: data.rangeFrom || 0,
      };
    }

    return {
      ...data,
      ...criteriaValues,
    };
  };

  updateCriteriaList = (data, listType, targetType, isRemoved = false) => {
    this.setState((prevState) => {
      const targetGroup = { ...prevState.targetGroup };
      let criteria = { ...targetGroup.criteria };
      const criteriaList = {
        ...data,
        targetGroupType: targetType,
      };
      const errors = [];
      if (isRemoved) {
        targetGroup.criteria = { ...criteria, [listType]: data };
        return {
          targetGroup,
          errors,
          isFormEdited: true,
        };
      }

      if (targetType === "location") {
        const locationCriteriaErrors = validateLocationCriteria(criteria, criteriaList, listType);
        if (locationCriteriaErrors.length === 0)
          criteria = {
            ...criteria,
            [listType]: [...criteria[listType], criteriaList],
          };
        errors.push(...locationCriteriaErrors);
      } else if (targetType === "theatre") {
        const theatreCriteriaErrors = validateTheatreCriteria(criteria, criteriaList, listType);
        if (theatreCriteriaErrors.length === 0)
          criteria = {
            ...criteria,
            [listType]: [...criteria[listType], this.updateDefaultValues(criteriaList)],
          };
        errors.push(...theatreCriteriaErrors);
      }

      targetGroup.criteria = this.sanitizeCriteriaEntries(criteria, listType);
      return {
        targetGroup,
        errors,
        isFormEdited: true,
      };
    });
  };

  sanitizeCriteriaEntries = (criteria, type) => {
    if (isEmpty(criteria)) return criteria;

    // Helper function to remove duplicates based on first tag ID
    const removeDuplicates = (items) => {
      const seenIds = new Set();
      return items.filter((item) => {
        if (!item.tags?.[0]) return true;
        const [id] = item.tags;
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      });
    };

    // Helper function to get IDs from items
    const getIds = (items) =>
      new Set(items.filter((item) => item.tags?.[0]).map((item) => item.tags[0]));

    let { inclusions = [], exclusions = [] } = criteria;

    switch (type) {
      case "inclusions": {
        // Remove duplicates from inclusions
        inclusions = removeDuplicates(inclusions);
        // Remove from exclusions any items that exist in inclusions
        const inclusionIds = getIds(inclusions);
        exclusions = exclusions.filter(
          (item) => !item.tags?.[0] || !inclusionIds.has(item.tags[0])
        );
        break;
      }
      case "exclusions": {
        // Remove duplicates from exclusions
        exclusions = removeDuplicates(exclusions);
        // Remove from inclusions any items that exist in exclusions
        const exclusionIds = getIds(exclusions);
        inclusions = inclusions.filter(
          (item) => !item.tags?.[0] || !exclusionIds.has(item.tags[0])
        );
        break;
      }
      default:
        // No sanitization needed for other types
        break;
    }

    return { inclusions, exclusions };
  };

  // TODO: Update URL once flow is completed
  handleTargetGroupDelete = (targetGroupId) => {
    const {
      actions,
      match: { params },
    } = this.props;
    actions.deleteTargetGroup(params, targetGroupId);
  };

  handleCampaignDelete = async () => {
    const {
      history,
      match: { params },
    } = this.props;
    const resp = await this.props.actions.deleteCampaign(params.campaignId);
    if (resp && resp.success) {
      history.push(`/campaigns`);
    }
  };

  handleTargetGroupCreate = async () => {
    const {
      actions,
      match: { params },
      history,
    } = this.props;
    history.push(`/campaign-create/${params.campaignId}/target-groups/create`);
    //SLATE-1444 Dec15 Anushree:- reset form on creation of new campaign
    this.handleFormReset();
    await actions.updateTargetGroupAction("create");
    if (this.containerRef.current) {
      this.containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  handleTargetGroupEdit = (targetGroupId) => {
    const {
      match: { params },
      actions,
      history,
    } = this.props;
    if (params.campaignId) {
      actions.getTargetGroupById(targetGroupId, "edit");
      history.push(`/campaign-create/${params.campaignId}/target-groups/${targetGroupId}`);
    }
  };

  handleTargetGroupDuplicate = (targetGroupId) => {
    const {
      match: { params },
      actions,
      history,
    } = this.props;

    actions.getTargetGroupById(targetGroupId, "duplicate");
    history.push(`/campaign-create/${params.campaignId}/target-groups/create?id=${targetGroupId}`);
  };

  handleFormReset = () => {
    const {
      match: {
        params: { action },
      },
      campaignCreate: { campaignTargetGroup },
    } = this.props;
    if (action !== "list" && action !== "create") {
      this.setState({
        targetGroup: campaignTargetGroup,
        hasFormSaved: false,
        isFormEdited: false,
      });
    } else {
      this.setState({
        ...this.getInitialState(),
        isPageLoading: false,
      });
    }
  };

  handleSaveTargetGroupAsDraft = async () => {
    const { targetGroup } = this.state;
    const {
      match: { params },
      actions,
    } = this.props;

    if (targetGroup.name) {
      targetGroup.campaignId = params.campaignId;
      await actions.createOrUpdateTargetGroup(params.campaignId, targetGroup, "edit");
    }

    this.setState({
      hasFormSaved: true,
      isFormEdited: false,
    });
  };

  handleChangeStep = (preferredStep) => {
    const {
      match: { params },
      history,
      campaignCreate: { activeStep, targetGroupAction, campaignTargetGroups },
      actions,
    } = this.props;
    const { targetGroup, isFormEdited } = this.state;
    const targetGroupCount =
      campaignTargetGroups.totalUnfilteredCount ??
      campaignTargetGroups.totalCount ??
      campaignTargetGroups.data.length;

    if (preferredStep === activeStep - 1) {
      if (targetGroupAction !== "list") {
        // Go back to target group list page without decrementing the step
        history.push(`/campaign-create/${params.campaignId}/target-groups/list`);
        return false;
      }
    }

    //SLATE-1417 Dec6 Anushree:- initial condition checks if movement is to direct next step, else executes campaignStepChange function.
    if (preferredStep === activeStep + 1 && params.campaignId) {
      if (targetGroupAction === "list") {
        //SLATE-1485 Jan3 Anushree:- Movement to Media page restricted if no targetGroup has been created
        if (targetGroupCount > 0) {
          history.push(`/campaign-create/${params.campaignId}/media/list`);
          return false;
        } else {
          showToast("Please Create Target Group", false);
        }
      }
      if (
        params.action !== "list" &&
        validateMandatoryTargetGroupFields(targetGroup, targetGroupAction)
      ) {
        targetGroup.campaignId = params.campaignId;
        //Only if form is edited then call update target group api
        if (isFormEdited) actions.createOrUpdateTargetGroup(params.campaignId, targetGroup, "list");
        else {
          actions.updateTargetGroupAction("list");
          actions.getNewCampaignTargetGroups(params);
        }
      } else {
      }
    } else {
      campaignStepChange(history, params, activeStep, preferredStep);
    }

    this.setState({
      hasFormSaved: true,
      isFormEdited: false,
    });
  };

  renderCampaignTree = () => {
    const {
      isCampaignInvalid,

      match: {
        params: { campaignId },
      },
    } = this.props;

    const { isValidTargetGroup } = this.state;
    if (isCampaignInvalid || !isValidTargetGroup) return null;
    return (
      <div className={campaignFormStyles.campaignTree}>
        <CampaignTree
          isNewCampaign
          // isLoading={false}
          // campaignTreeData={{
          //   ...campaign,
          //   targetGroups: targetGroupAction === "list" ? campaignTargetGroups?.data : [targetGroup],
          // }}
          // campaignCreate={campaignCreate}
          campaignIdForSummary={campaignId}
        />
      </div>
    );
  };

  renderTargetGroupSection = () => {
    const {
      isCampaignInvalid,
      actions,
      campaignCreate: {
        campaign,
        campaignTargetGroups,
        targetGroupAction,
        isTargetGroupLoading,
        isTargetGroupDetailsLoading,
      },
    } = this.props;
    const { targetGroup, hasFormSaved, errors, isPageLoading, isValidTargetGroup } = this.state;
    if (isCampaignInvalid || !isValidTargetGroup)
      return <EmptyState type={isValidTargetGroup ? "campaign" : "TargetGroup"} />;

    if (isPageLoading) return <InfoBlockLoading />;

    if (targetGroupAction === "list") {
      return (
        <CampaignCreateTargetGroupListWithFilters
          history={this.props.history}
          match={this.props.match}
          actions={actions}
          targetGroups={campaignTargetGroups?.data}
          totalCount={campaignTargetGroups?.totalCount || 0}
          onEdit={this.handleTargetGroupEdit}
          onDuplicate={this.handleTargetGroupDuplicate}
          onDelete={this.handleTargetGroupDelete}
          showAdd
          campaign={campaign}
          isLoading={isTargetGroupLoading}
          isDetailsLoading={isTargetGroupDetailsLoading}
          countries={campaign.countries}
          getTargetGroupDetails={actions.getTargetGroupById}
          onTargetGroupCreate={this.handleTargetGroupCreate}
          showSplit
          onSplit={this.handleTargetGroupSplit}
          campaignId={campaign.id}
        />
      );
    } else {
      return (
        <CreateTargetGroup
          campaign={campaign}
          targetGroups={campaignTargetGroups.data}
          hasFormSaved={hasFormSaved}
          targetGroup={targetGroup}
          onInputChange={this.onInputChange}
          criteria={targetGroup.criteria}
          errors={errors}
          onCriteriaListUpdate={this.updateCriteriaList}
          companyId={this.props.userData.company.id}
          targetGroupAction={targetGroupAction}
        />
      );
    }
  };
  //method to handle the target group split
  handleTargetGroupSplit = (targetGroupId) => {
    const {
      campaignCreate: {
        campaignTargetGroups: { data },
      },
    } = this.props;
    //finding the targetGroup validity for which the split will occur
    const targetGroupValidityForSplit = data.find((item) => item.id === targetGroupId)?.validity;
    this.setState({
      isSplitPanelOpen: true,
      targetGroupSplitData: { id: targetGroupId, validity: targetGroupValidityForSplit },
    });
  };

  closeSplitPanel = () => {
    this.setState({
      isSplitPanelOpen: false,
      targetGroupValidityForSplit: { id: "", validity: {} },
    });
  };

  render() {
    const {
      history,
      campaignCreate: {
        isTargetGroupLoading,
        campaign,
        activeStep,
        campaignTargetGroups,
        targetGroupAction,
      },
      match: { params },
    } = this.props;
    const {
      hasFormSaved,
      isFormEdited,
      isPageLoading,
      isSplitPanelOpen,
      targetGroupSplitData,
    } = this.state;
    const targetGroupCount =
      campaignTargetGroups.totalUnfilteredCount ??
      campaignTargetGroups.totalCount ??
      campaignTargetGroups.data.length;

    if (isTargetGroupLoading && isPageLoading) return <PageLoadingWithTabs />;

    const headerTitle =
      !campaign.id || (targetGroupAction === "list" && !campaign.id)
        ? "PageHeader.newTargetGroup"
        : campaign.name;

    return (
      <div ref={this.containerRef} className={`col-12 clearfix ${styles.pageWrapper}`}>
        <div className={styles.container}>
          <Breadcrumb
            history={history}
            links={[
              { name: "PageHeader.campaigns", path: "/campaigns" },
              { name: headerTitle, path: `/campaigns/campaigns/${campaign.id}/details` },
              { name: "PageHeader.editTargetGroup" },
            ]}
          />
          <div className={classNames("col-12 clearfix")}>
            <div className={campaignFormStyles.campaignCreateProgressBar}>
              <CampaignPageHeader
                title={headerTitle}
                onSaveCampaignAsDraft={this.handleSaveTargetGroupAsDraft}
                onDiscardCampaign={this.handleCampaignDelete}
                isDiscardDisabled={!campaign.id || campaign.status !== CAMPAIGN_STATUSES.DRAFT}
                hideDraftButton
              />
              <CampaignSteps
                history={history}
                activeStep={activeStep}
                validTillStep={targetGroupCount > 0 ? activeStep + 1 : activeStep}
                hasFormSaved={hasFormSaved}
                disableSave={!campaign.id}
                disableReset={!isFormEdited}
                onChangeStep={this.handleChangeStep}
                onReset={this.handleFormReset}
                isAdminUser={this.validateCreateCampaignAccess}
                onSaveDraft={() => {}}
              />
            </div>
            <div className={classNames("col col-12", campaignFormStyles.pageContent)}>
              {this.renderTargetGroupSection()}
              {this.renderCampaignTree()}
            </div>
            {isSplitPanelOpen && (
              <SplitTargetGroup
                validity={targetGroupSplitData.validity}
                header="RightPanelHeader.splitTargetGroup"
                isOpen={isSplitPanelOpen}
                onClose={this.closeSplitPanel}
                splitCampaigns={(data) => {
                  this.props.actions.splitTargetGroup(params, targetGroupSplitData.id, data);
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = createSelector(
  (state) => state.userData,
  (state) => state.campaignCreate,
  (userData, campaignCreate) => ({
    userData: userData.user,
    campaignCreate: campaignCreate,
  })
);

export default connect(mapStateToProps, bindDispatch)(campaignRouteHandler(TargetGroup));
